import { getRecommendedImageQuality, getRecommendedMaxDimensions } from './memoryMonitor';

/**
 * Compress and resize an image to reduce file size with memory-aware defaults
 * @param base64Image - Base64 encoded image string
 * @param maxWidth - Maximum width in pixels (adaptive if not specified)
 * @param maxHeight - Maximum height in pixels (adaptive if not specified)
 * @param quality - JPEG quality 0-1 (adaptive if not specified)
 * @returns Compressed blob
 */
export const compressImage = async (
  base64Image: string,
  maxWidth?: number,
  maxHeight?: number,
  quality?: number
): Promise<Blob> => {
  // Use memory-aware defaults if not specified
  const dims = getRecommendedMaxDimensions();
  const finalMaxWidth = maxWidth || dims.width;
  const finalMaxHeight = maxHeight || dims.height;
  const finalQuality = quality || getRecommendedImageQuality();

  return new Promise((resolve, reject) => {
    const img = new Image();
    
    const cleanup = () => {
      img.onload = null;
      img.onerror = null;
      img.src = '';
    };
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        cleanup();
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Calculate new dimensions while maintaining aspect ratio
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > finalMaxWidth) {
          height = Math.round((height * finalMaxWidth) / width);
          width = finalMaxWidth;
        }
      } else {
        if (height > finalMaxHeight) {
          width = Math.round((width * finalMaxHeight) / height);
          height = finalMaxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      
      // Return blob directly instead of base64 to save memory
      canvas.toBlob((blob) => {
        // Clean up canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        canvas.width = 0;
        canvas.height = 0;
        cleanup();
        
        if (!blob) {
          reject(new Error('Failed to create blob'));
          return;
        }
        resolve(blob);
      }, 'image/jpeg', finalQuality);
    };

    img.onerror = () => {
      cleanup();
      reject(new Error('Failed to load image'));
    };

    img.src = base64Image;
  });
};

/**
 * Convert base64 to blob for uploading to storage
 * @param base64Image - Base64 encoded image string
 * @returns Blob object
 */
export const base64ToBlob = async (base64Image: string): Promise<Blob> => {
  const response = await fetch(base64Image);
  return response.blob();
};

/**
 * Create an object URL from a blob for display
 * @param blob - Blob object
 * @returns Object URL string
 */
export const createObjectURL = (blob: Blob): string => {
  return URL.createObjectURL(blob);
};

/**
 * Compress a blob image to reduce file size with memory-aware defaults
 * @param blob - Image blob
 * @param maxWidth - Maximum width in pixels (adaptive if not specified)
 * @param maxHeight - Maximum height in pixels (adaptive if not specified)
 * @param quality - JPEG quality 0-1 (adaptive if not specified)
 * @returns Compressed blob
 */
export const compressImageBlob = async (
  blob: Blob,
  maxWidth?: number,
  maxHeight?: number,
  quality?: number
): Promise<Blob> => {
  // Use memory-aware defaults if not specified
  const dims = getRecommendedMaxDimensions();
  const finalMaxWidth = maxWidth || dims.width;
  const finalMaxHeight = maxHeight || dims.height;
  const finalQuality = quality || getRecommendedImageQuality();

  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectURL = URL.createObjectURL(blob);
    
    const cleanup = () => {
      URL.revokeObjectURL(objectURL);
      img.onload = null;
      img.onerror = null;
      img.src = '';
    };
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        cleanup();
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Calculate new dimensions while maintaining aspect ratio
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > finalMaxWidth) {
          height = Math.round((height * finalMaxWidth) / width);
          width = finalMaxWidth;
        }
      } else {
        if (height > finalMaxHeight) {
          width = Math.round((width * finalMaxHeight) / height);
          height = finalMaxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      
      // Return blob directly
      canvas.toBlob((compressedBlob) => {
        // Clean up canvas and image
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        canvas.width = 0;
        canvas.height = 0;
        cleanup();
        
        if (!compressedBlob) {
          reject(new Error('Failed to create blob'));
          return;
        }
        resolve(compressedBlob);
      }, 'image/jpeg', finalQuality);
    };

    img.onerror = () => {
      cleanup();
      reject(new Error('Failed to load image'));
    };

    img.src = objectURL;
  });
};
